/**
 * Context Panel merge helpers.
 *
 * Implements plan item #5: merge the current episode's linked_entities with
 * the entity-graph's episode list for the entity on screen. Dedupe by
 * episode_id, union link_kinds, and rank-sort the kinds.
 *
 * See docs/plans/context-panel-from-graph.md plan item #5 & #11.
 */

/** Stable rank — lower index = higher priority. Unknown kinds pushed to tail. */
export const LINK_KIND_RANK: readonly string[] = [
  "started_with",
  "created",
  "triggered_by",
  "modified",
  "mentions",
  "reply_to",
];

export function rankLinkKinds(kinds: readonly string[]): string[] {
  const unique = Array.from(new Set(kinds));
  return unique.sort((a, b) => {
    const ra = LINK_KIND_RANK.indexOf(a);
    const rb = LINK_KIND_RANK.indexOf(b);
    const va = ra === -1 ? LINK_KIND_RANK.length : ra;
    const vb = rb === -1 ? LINK_KIND_RANK.length : rb;
    if (va !== vb) return va - vb;
    // Stable tail order: unknown kinds retain original encounter order.
    return kinds.indexOf(a) - kinds.indexOf(b);
  });
}

export interface EpisodeLinkRow {
  readonly episode_id: string;
  readonly title?: string;
  readonly status?: string;
  readonly link_kinds: readonly string[];
  readonly updated_at?: string;
  readonly is_empty?: boolean;
}

export interface MergeInput {
  readonly fromEntityGraph: readonly EpisodeLinkRow[];
  readonly fromCurrentEpisode: readonly EpisodeLinkRow[];
}

/**
 * Merge entity-graph rows and current-episode rows. Dedupe by episode_id,
 * union link_kinds with stable rank order.
 */
export function mergeContextPanelEpisodes(input: MergeInput): EpisodeLinkRow[] {
  const byId = new Map<string, { base: EpisodeLinkRow; kinds: string[] }>();

  // entity-graph first — authoritative for title/status/updated_at
  for (const row of input.fromEntityGraph) {
    const existing = byId.get(row.episode_id);
    if (existing) {
      existing.kinds.push(...row.link_kinds);
    } else {
      byId.set(row.episode_id, { base: row, kinds: [...row.link_kinds] });
    }
  }

  for (const row of input.fromCurrentEpisode) {
    const existing = byId.get(row.episode_id);
    if (existing) {
      existing.kinds.push(...row.link_kinds);
    } else {
      byId.set(row.episode_id, { base: row, kinds: [...row.link_kinds] });
    }
  }

  return Array.from(byId.values()).map(({ base, kinds }) => ({
    ...base,
    link_kinds: rankLinkKinds(kinds),
  }));
}
